import { TabSection } from './TabSection'
import { ChipInput } from '../ChipInput'
import { Icon } from '../Icon'
import type { ChannelConfigUpdate, FilterMode, PrSizeTier } from '../../types'
import styles from './FiltersTab.module.css'

const PR_SIZE_TIERS: PrSizeTier[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL']

interface FilterBlockProps {
  label: string
  hint: string
  inputPlaceholder: string
  inputHint: string
  modePlaceholders?: Partial<Record<FilterMode, string>>
  mode: FilterMode | null
  patterns: string[]
  onModeChange: (mode: FilterMode | null) => void
  onPatternsChange: (patterns: string[]) => void
  modeHints?: Partial<Record<FilterMode, string>>
}

function FilterBlock({
  label, hint, inputPlaceholder, inputHint, modePlaceholders,
  mode, patterns, onModeChange, onPatternsChange, modeHints,
}: FilterBlockProps) {
  const activePlaceholder = (mode && modePlaceholders?.[mode]) ?? inputPlaceholder
  const isEmpty = mode !== null && patterns.length === 0

  function selectMode(next: FilterMode | null) {
    if (next === mode) return
    // Switching mode clears patterns — user starts fresh
    if (next !== null && mode !== null && next !== mode) {
      onPatternsChange([])
    }
    onModeChange(next)
  }

  return (
    <div className={styles.block}>
      <div className={styles.blockLabel}>{label}</div>
      <div className={styles.blockHint}>{hint}</div>

      <div className={styles.modeRow}>
        {(['off', 'ALLOWLIST', 'DENYLIST'] as const).map(opt => {
          const active = opt === 'off' ? mode === null : mode === opt
          return (
            <button
              key={opt}
              className={`${styles.modeBtn} ${active ? styles.modeBtnActive : ''}`}
              onClick={() => selectMode(opt === 'off' ? null : opt)}
            >
              {opt === 'off' ? 'Off' : opt === 'ALLOWLIST' ? 'Allow only' : 'Block'}
            </button>
          )
        })}
      </div>

      {mode !== null && (
        <div className={styles.inputWrap}>
          <ChipInput
            label=""
            values={patterns}
            onChange={onPatternsChange}
            placeholder={activePlaceholder}
            hint={inputHint}
          />
          {isEmpty && (
            <div className={styles.emptyWarning}>
              Add at least one pattern to activate this filter
            </div>
          )}
          {modeHints?.[mode] && (
            <div className={styles.modeHint}>{modeHints[mode]}</div>
          )}
        </div>
      )}
    </div>
  )
}

interface SizeBlockProps {
  mode: FilterMode | null
  tiers: PrSizeTier[]
  onModeChange: (mode: FilterMode | null) => void
  onTiersChange: (tiers: PrSizeTier[]) => void
}

function SizeBlock({ mode, tiers, onModeChange, onTiersChange }: SizeBlockProps) {
  const isEmpty = mode !== null && tiers.length === 0

  function selectMode(next: FilterMode | null) {
    if (next === mode) return
    // Switching mode clears tiers — user starts fresh
    if (next !== null && mode !== null && next !== mode) {
      onTiersChange([])
    }
    onModeChange(next)
  }

  function toggleTier(tier: PrSizeTier) {
    onTiersChange(
      tiers.includes(tier) ? tiers.filter(t => t !== tier) : [...tiers, tier]
    )
  }

  return (
    <div className={styles.block}>
      <div className={styles.blockLabel}>Size</div>
      <div className={styles.blockHint}>
        Control which PR size tiers allow or block notifications for this channel — based on lines changed
      </div>

      <div className={styles.modeRow}>
        {(['off', 'ALLOWLIST', 'DENYLIST'] as const).map(opt => {
          const active = opt === 'off' ? mode === null : mode === opt
          return (
            <button
              key={opt}
              className={`${styles.modeBtn} ${active ? styles.modeBtnActive : ''}`}
              onClick={() => selectMode(opt === 'off' ? null : opt)}
            >
              {opt === 'off' ? 'Off' : opt === 'ALLOWLIST' ? 'Allow only' : 'Block'}
            </button>
          )
        })}
      </div>

      {mode !== null && (
        <div className={styles.inputWrap}>
          <div className={styles.tierRow}>
            {PR_SIZE_TIERS.map(tier => {
              const on = tiers.includes(tier)
              return (
                <button
                  key={tier}
                  className={`${styles.tierPill} ${on ? styles.tierPillOn : ''}`}
                  onClick={() => toggleTier(tier)}
                >
                  {tier}
                </button>
              )
            })}
          </div>
          {isEmpty && (
            <div className={styles.emptyWarning}>
              Add at least one size to activate this filter
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface Props {
  draft: {
    repoFilterMode?: FilterMode | null
    filteredRepos?: string[]
    labelFilterMode?: FilterMode | null
    filteredLabels?: string[]
    sizeFilterMode?: FilterMode | null
    filteredSizes?: PrSizeTier[]
    authorFilterMode?: FilterMode | null
    filteredAuthors?: string[]
  }
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
}

export function FiltersTab({ draft, onChange }: Props) {
  const repoMode = draft.repoFilterMode ?? null
  const repoPatterns = draft.filteredRepos ?? []
  const labelMode = draft.labelFilterMode ?? null
  const labelPatterns = draft.filteredLabels ?? []
  const sizeMode = draft.sizeFilterMode ?? null
  const sizeTiers = draft.filteredSizes ?? []
  const authorMode = draft.authorFilterMode ?? null
  const authorPatterns = draft.filteredAuthors ?? []

  return (
    <TabSection
      accent="teal"
      icon="filter_alt"
      title="Filters"
      subtitle="Scope which PRs reach this channel by repository, label, or size"
    >
      <FilterBlock
        label="Repository"
        hint="Control which repositories contribute PR notifications to this channel"
        inputPlaceholder="e.g. my-repo or toasttab/service-*"
        inputHint="Partial names expand to toasttab/my-repo — glob patterns like toasttab/service-* are supported"
        mode={repoMode}
        patterns={repoPatterns}
        onModeChange={m => onChange({ repoFilterMode: m, filteredRepos: m === null ? [] : repoPatterns })}
        onPatternsChange={p => onChange({ filteredRepos: p })}
      />

      <div className={styles.divider} />

      <FilterBlock
        label="Label"
        hint="Control which GitHub PR labels allow or block notifications for this channel"
        inputPlaceholder="e.g. a GitHub label"
        modePlaceholders={{
          ALLOWLIST: 'e.g. urgent-review,  ready-for-review',
          DENYLIST:  'e.g. do-not-merge,  wip',
        }}
        inputHint="Case-insensitive — matches GitHub PR labels exactly"
        mode={labelMode}
        patterns={labelPatterns}
        onModeChange={m => onChange({ labelFilterMode: m, filteredLabels: m === null ? [] : labelPatterns })}
        onPatternsChange={p => onChange({ filteredLabels: p })}
        modeHints={{
          ALLOWLIST: 'Tracking starts when a matching label is present at open or is added later. Removing the label afterwards won\'t stop updates already in flight.',
          DENYLIST:  'Tracking starts when a blocked label is absent at open or is removed later. Adding the label afterwards won\'t stop updates already in flight.',
        }}
      />
      <div className={styles.divider} />

      <SizeBlock
        mode={sizeMode}
        tiers={sizeTiers}
        onModeChange={m => onChange({ sizeFilterMode: m, filteredSizes: m === null ? [] : sizeTiers })}
        onTiersChange={t => onChange({ filteredSizes: t })}
      />
      <div className={styles.divider} />

      <FilterBlock
        label="Author"
        hint="Control which GitHub PR authors allow or block notifications for this channel"
        inputPlaceholder="e.g. a GitHub username"
        modePlaceholders={{
          ALLOWLIST: 'e.g. octocat, hubot',
          DENYLIST:  'e.g. dependabot[bot], github-actions[bot]',
        }}
        inputHint="Case-insensitive — matches GitHub usernames exactly"
        mode={authorMode}
        patterns={authorPatterns}
        onModeChange={m => onChange({ authorFilterMode: m, filteredAuthors: m === null ? [] : authorPatterns })}
        onPatternsChange={p => onChange({ filteredAuthors: p })}
      />

      <div className={styles.notes}>
        <div className={styles.note}>
          <Icon name="info" size="sm" className={styles.noteIcon} />
          A PR must pass every active filter to reach this channel — repo, label, size, and author filters are all applied together.
        </div>
        <div className={styles.note}>
          <Icon name="history" size="sm" className={styles.noteIcon} />
          PRs already being tracked when filters are set will continue to receive updates until they are merged or closed.
        </div>
        <div className={styles.note}>
          <Icon name="notifications_off" size="sm" className={styles.noteIcon} />
          Not receiving a notification even though your filters pass? Check that Review Updates are enabled for that event type in the Review Updates tab.
        </div>
      </div>
    </TabSection>
  )
}
